/* eslint-disable camelcase */
import {
  ALL_PORTFOLIO_USERS,
  PORTFOLIO_USER_ROLE_ADMIN,
  PORTFOLIO_USER_ROLE_OWNER,
  PORTFOLIO_USER_ROLE_STANDARD,
  PORTFOLIO_USER_ROLE_VIEWER,
  TRADE_RULE_STATE_ACCEPTED,
  TRADE_RULE_STATE_CANCELLED, TRADE_RULE_STATE_CLOSED,
  TRADE_RULE_STATE_PROPOSED, TRADE_RULE_STATE_REJECTED,
  TRADE_TYPE_COMMUNITY, TRADE_TYPE_CONTRACTED,
  TRADE_TYPE_NOMINATED, TRADE_TYPE_RESIDUAL,
  UNTRADED_ENERGY_KEY,
} from '../constants';

// Day
export const DAY_MONDAY = 'MONDAY';
export const DAY_TUESDAY = 'TUESDAY';
export const DAY_WEDNESDAY = 'WEDNESDAY';
export const DAY_THURSDAY = 'THURSDAY';
export const DAY_FRIDAY = 'FRIDAY';
export const DAY_SATURDAY = 'SATURDAY';
export const DAY_SUNDAY = 'SUNDAY';
export const DAY_PUBLIC_HOLIDAY = 'PUBLIC_HOLIDAY';

export const getDayNames = (formatMessage) => {
  const DayNames = {
    [DAY_MONDAY]: formatMessage({ id: 'day_name_monday', defaultMessage: 'Monday' }),
    [DAY_TUESDAY]: formatMessage({ id: 'day_name_tuesday', defaultMessage: 'Tuesday' }),
    [DAY_WEDNESDAY]: formatMessage({ id: 'day_name_wednesday', defaultMessage: 'Wednesday' }),
    [DAY_THURSDAY]: formatMessage({ id: 'day_name_thursday', defaultMessage: 'Thursday' }),
    [DAY_FRIDAY]: formatMessage({ id: 'day_name_friday', defaultMessage: 'Friday' }),
    [DAY_SATURDAY]: formatMessage({ id: 'day_name_saturday', defaultMessage: 'Saturday' }),
    [DAY_SUNDAY]: formatMessage({ id: 'day_name_sunday', defaultMessage: 'Sunday' }),
    [DAY_PUBLIC_HOLIDAY]: formatMessage({ id: 'day_name_publicholiday', defaultMessage: 'Public Holiday' }),
    // [DAY_PUBLIC_HOLIDAY]: 'Public Holiday',
  };
  return DayNames;
};

// Month
export const MONTH_JANUARY = 'JANUARY';
export const MONTH_FEBRUARY = 'FEBRUARY';
export const MONTH_MARCH = 'MARCH';
export const MONTH_APRIL = 'APRIL';
export const MONTH_MAY = 'MAY';
export const MONTH_JUNE = 'JUNE';
export const MONTH_JULY = 'JULY';
export const MONTH_AUGUST = 'AUGUST';
export const MONTH_SEPTEMBER = 'SEPTEMBER';
export const MONTH_OCTOBER = 'OCTOBER';
export const MONTH_NOVEMBER = 'NOVEMBER';
export const MONTH_DECEMBER = 'DECEMBER';

export const getMonthNames = (formatMessage) => {
  const MonthNames = {
    [MONTH_JANUARY]: formatMessage({ id: 'month_name_january', defaultMessage: 'January' }),
    [MONTH_FEBRUARY]: formatMessage({ id: 'month_name_february', defaultMessage: 'February' }),
    [MONTH_MARCH]: formatMessage({ id: 'month_name_march', defaultMessage: 'March' }),
    [MONTH_APRIL]: formatMessage({ id: 'month_name_april', defaultMessage: 'April' }),
    [MONTH_MAY]: formatMessage({ id: 'month_name_may', defaultMessage: 'May' }),
    [MONTH_JUNE]: formatMessage({ id: 'month_name_june', defaultMessage: 'June' }),
    [MONTH_JULY]: formatMessage({ id: 'month_name_july', defaultMessage: 'July' }),
    [MONTH_AUGUST]: formatMessage({ id: 'month_name_august', defaultMessage: 'August' }),
    [MONTH_SEPTEMBER]: formatMessage({ id: 'month_name_september', defaultMessage: 'September' }),
    [MONTH_OCTOBER]: formatMessage({ id: 'month_name_october', defaultMessage: 'October' }),
    [MONTH_NOVEMBER]: formatMessage({ id: 'month_name_november', defaultMessage: 'November' }),
    [MONTH_DECEMBER]: formatMessage({ id: 'month_name_december', defaultMessage: 'December' }),
  };
  return MonthNames;
};

/**
 * Returns the decimal and thousands separator configuration for a given locale.
 * @param {string} locale - The locale string (e.g., 'en-AU', 'it', 'ja', 'fr-FR')
 * @returns {{ decimal: string, thousands: string, grouping: number[] }}
 *   Configuration object with decimal separator, thousands separator, and grouping pattern
 */
export const getLocaleNumberFormat = (locale) => {
  const defaultFormat = {
    decimal: '.',
    thousands: ',',
    grouping: [3],
  };

  if (!locale || typeof locale !== 'string') {
    return defaultFormat;
  }

  try {
    const normalizedLocale = locale.toLowerCase().split('-x-')[0];
    const parts = new Intl.NumberFormat(normalizedLocale).formatToParts(1234567.89);
    const decimal = parts.find(({ type }) => type === 'decimal')?.value;
    const thousands = parts.find(({ type }) => type === 'group')?.value;
    const integerParts = parts.filter(({ type }) => type === 'integer');
    const grouping = integerParts[integerParts.length - 1]?.value.length;

    return {
      decimal: decimal || defaultFormat.decimal,
      thousands: thousands || defaultFormat.thousands,
      grouping: grouping ? [grouping] : defaultFormat.grouping,
    };
  } catch (error) {
    return defaultFormat;
  }
};

export const getChartLocale = (formatMessage, locale) => {
  const numberFormat = getLocaleNumberFormat(locale);

  const localeConfig = {
    currency: [formatMessage({ id: 'common.entities.currency.label', defaultMessage: '$' }), ''],
    // Number formatting properties for d3-format
    decimal: numberFormat.decimal,
    thousands: numberFormat.thousands,
    grouping: numberFormat.grouping,
    // Time formatting properties for d3-time-format
    periods: [formatMessage({ id: 'locale_am', defaultMessage: 'AM' }), formatMessage({ id: 'locale_pm', defaultMessage: 'PM' })],
    // Weekdays should be in the order of Sun - Sat Ref: https://d3js.org/d3-time-format#timeFormatLocale
    days: [
      formatMessage({ id: 'day_name_sunday', defaultMessage: 'Sunday' }),
      formatMessage({ id: 'day_name_monday', defaultMessage: 'Monday' }),
      formatMessage({ id: 'day_name_tuesday', defaultMessage: 'Tuesday' }),
      formatMessage({ id: 'day_name_wednesday', defaultMessage: 'Wednesday' }),
      formatMessage({ id: 'day_name_thursday', defaultMessage: 'Thursday' }),
      formatMessage({ id: 'day_name_friday', defaultMessage: 'Friday' }),
      formatMessage({ id: 'day_name_saturday', defaultMessage: 'Saturday' })],
    // Weekdays should be in the order of Sun - Sat Ref: https://d3js.org/d3-time-format#timeFormatLocale
    shortDays: [
      formatMessage({ id: 'day_name_short_sunday', defaultMessage: 'Sun' }),
      formatMessage({ id: 'day_name_short_monday', defaultMessage: 'Mon' }),
      formatMessage({ id: 'day_name_short_tuesday', defaultMessage: 'Tue' }),
      formatMessage({ id: 'day_name_short_wednesday', defaultMessage: 'Wed' }),
      formatMessage({ id: 'day_name_short_thursday', defaultMessage: 'Thu' }),
      formatMessage({ id: 'day_name_short_friday', defaultMessage: 'Fri' }),
      formatMessage({ id: 'day_name_short_saturday', defaultMessage: 'Sat' }),
    ],
    months: [
      formatMessage({ id: 'month_name_january', defaultMessage: 'January' }),
      formatMessage({ id: 'month_name_february', defaultMessage: 'February' }),
      formatMessage({ id: 'month_name_march', defaultMessage: 'March' }),
      formatMessage({ id: 'month_name_april', defaultMessage: 'April' }),
      formatMessage({ id: 'month_name_may', defaultMessage: 'May' }),
      formatMessage({ id: 'month_name_june', defaultMessage: 'June' }),
      formatMessage({ id: 'month_name_july', defaultMessage: 'July' }),
      formatMessage({ id: 'month_name_august', defaultMessage: 'August' }),
      formatMessage({ id: 'month_name_september', defaultMessage: 'September' }),
      formatMessage({ id: 'month_name_october', defaultMessage: 'October' }),
      formatMessage({ id: 'month_name_november', defaultMessage: 'November' }),
      formatMessage({ id: 'month_name_december', defaultMessage: 'December' }),
    ],
    shortMonths: [
      formatMessage({ id: 'month_name_short_january', defaultMessage: 'Jan' }),
      formatMessage({ id: 'month_name_short_february', defaultMessage: 'Feb' }),
      formatMessage({ id: 'month_name_short_march', defaultMessage: 'Mar' }),
      formatMessage({ id: 'month_name_short_april', defaultMessage: 'Apr' }),
      formatMessage({ id: 'month_name_short_may', defaultMessage: 'May' }),
      formatMessage({ id: 'month_name_short_june', defaultMessage: 'Jun' }),
      formatMessage({ id: 'month_name_short_july', defaultMessage: 'Jul' }),
      formatMessage({ id: 'month_name_short_august', defaultMessage: 'Aug' }),
      formatMessage({ id: 'month_name_short_september', defaultMessage: 'Sep' }),
      formatMessage({ id: 'month_name_short_october', defaultMessage: 'Oct' }),
      formatMessage({ id: 'month_name_short_november', defaultMessage: 'Nov' }),
      formatMessage({ id: 'month_name_short_december', defaultMessage: 'Dec' }),
    ],
  };
  return localeConfig;
};

/**
 * Builds the d3 number configuration for currency charts from the partner currency formatter.
 * @param {import('react-intl').IntlShape} intlCurrency - Partner currency formatter.
 * @returns {{currency: string[], decimal: string, thousands: string, grouping: number[]}}
 *   Currency-specific number configuration.
 */
export const getCurrencyChartLocale = (intlCurrency) => {
  const numberFormat = getLocaleNumberFormat(intlCurrency?.locale);
  const currencySymbol = intlCurrency?.formatMessage(
    { id: 'common.entities.currency.label', defaultMessage: '$' },
  ) || '$';
  const formattedCurrency = intlCurrency?.formatMessage?.(
    {
      id: 'common.currency.value',
      defaultMessage: '{n, number, :: .00 currency/AUD}',
    },
    { n: 1234567.89 },
  );
  const firstNumberIndex = typeof formattedCurrency === 'string'
    ? formattedCurrency.search(/\p{Number}/u)
    : -1;
  const lastNumberIndex = typeof formattedCurrency === 'string'
    ? formattedCurrency.search(/\p{Number}(?!.*\p{Number})/u)
    : -1;
  const currency = firstNumberIndex === -1 || lastNumberIndex === -1
    ? [currencySymbol, '']
    : [
      formattedCurrency.slice(0, firstNumberIndex),
      formattedCurrency.slice(lastNumberIndex + 1),
    ];

  return {
    currency,
    decimal: numberFormat.decimal,
    thousands: numberFormat.thousands,
    grouping: numberFormat.grouping,
  };
};

export const decimalSeparator = {
  it: ',',
};

/**
 * Takes in a trade rule state and the intl object and returns the translated label.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {TRADE_RULE_STATE_PROPOSED | TRADE_RULE_STATE_ACCEPTED
 * | TRADE_RULE_STATE_CANCELLED | TRADE_RULE_STATE_REJECTED
 * | TRADE_RULE_STATE_CLOSED} state - Trade rule state.
 * @returns {(string | null)} - translated trade rule state label, or null if not a valid state
 * to translate or no intl present.
 */
export const getTradeRuleStateLabel = (intl, state) => {
  if (!intl) {
    return null;
  }

  if (!state) {
    return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_unspecified', defaultMessage: 'Unspecified' });
  }

  switch (state) {
    case TRADE_RULE_STATE_PROPOSED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_proposed', defaultMessage: 'Proposed' });

    case TRADE_RULE_STATE_ACCEPTED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_accepted', defaultMessage: 'Accepted' });

    case TRADE_RULE_STATE_CANCELLED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_cancelled', defaultMessage: 'Cancelled' });

    case TRADE_RULE_STATE_REJECTED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_rejected', defaultMessage: 'Rejected' });

    case TRADE_RULE_STATE_CLOSED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_closed', defaultMessage: 'Closed' });

    default:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_rule_state_unspecified', defaultMessage: 'Unspecified' });
  }
};

/**
 * Takes in a trade type and the intl object and returns the translated label.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {TRADE_TYPE_COMMUNITY | TRADE_TYPE_CONTRACTED
 * | TRADE_TYPE_NOMINATED | TRADE_TYPE_RESIDUAL
 * | UNTRADED_ENERGY_KEY} type - Trade type.
 * @returns {(string | null)} - translated trade type label, or null if not a valid type
 * to translate or no intl present.
 */
export const getTradeTypeLabel = (intl, type) => {
  if (!type || !intl) {
    return null;
  }

  switch (type) {
    case TRADE_TYPE_COMMUNITY:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_type_community', defaultMessage: 'Community' });

    case TRADE_TYPE_CONTRACTED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_type_contracted', defaultMessage: 'Contracted' });

    case TRADE_TYPE_NOMINATED:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_type_nominated', defaultMessage: 'Peer-to-peer' });

    case TRADE_TYPE_RESIDUAL:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_type_residual', defaultMessage: 'Retailer default' });

    case UNTRADED_ENERGY_KEY:
      return intl.formatMessage({ id: 'util.i18n.constants.get_trade_type_label.untraded', defaultMessage: 'Untraded energy' });

    default:
      return intl.formatMessage({ id: 'util.i18n.constants.trade_type_unspecified', defaultMessage: 'Unspecified' });
  }
};

/**
 * Returns the translated "Other {tradeType}" label for a typed "Other"
 * chart sub-series. Delegates to `getTradeTypeLabel` to translate the type name,
 * then interpolates it into the tooltip template.
 * @param {import('react-intl').IntlShape} intl - i18n intl object.
 * @param {TRADE_TYPE_COMMUNITY | TRADE_TYPE_CONTRACTED
 * | TRADE_TYPE_NOMINATED | TRADE_TYPE_RESIDUAL
 * | UNTRADED_ENERGY_KEY} tradeType - Trade type.
 * @returns {(string | null)} - Translated "Other {tradeType}" label, or null when
 *   `intl` or `tradeType` is missing.
 */
export const formatOtherTradeTypeLabel = (intl, tradeType) => {
  if (!intl || !tradeType) return null;
  return intl.formatMessage(
    { id: 'util.i18n.constants.other_trade_type_label', defaultMessage: 'Other {tradeType}' },
    { tradeType: getTradeTypeLabel(intl, tradeType) },
  );
};

/**
 * Returns a memoization-friendly callback `(tradeType, alsoVisible) => string`
 * for use with `getFilteredTradeChartDataByVisibleKeys`. When `alsoVisible` is
 * true the label is "Other {type}" (e.g. "Other Contracted"); when false it is
 * just the trade type name (e.g. "Contracted"), indicating the type exists only
 * in the hidden bucket with no visible counterpart.
 *
 * Extracting this factory avoids duplicating the identical `useCallback` body
 * in every chart component that calls `useFilteredChartData`.
 * @param {import('react-intl').IntlShape} intl - i18n intl object.
 * @returns {(tradeType: string, alsoVisible: boolean) => string} Label-builder callback.
 */
export const buildOtherSubSeriesLabelGetter = (intl) => (
  (tradeType, alsoVisible) => (alsoVisible
    ? formatOtherTradeTypeLabel(intl, tradeType)
    : getTradeTypeLabel(intl, tradeType))
);

/**
 * Takes in a key and the intl object and returns the translated key.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {ALL_PORTFOLIO_USERS | PORTFOLIO_USER_ROLE_OWNER
 * | PORTFOLIO_USER_ROLE_ADMIN | PORTFOLIO_USER_ROLE_STANDARD
 * |PORTFOLIO_USER_ROLE_VIEWER } role - Portfolio user role.
 * @returns {(string | null)} - translated user role, or null if not a valid role
 * to translate or no intl present.
 */
export const getPortfolioUserRoleLabel = (intl, role) => {
  if (!role || !intl) {
    return null;
  }

  switch (role) {
    case PORTFOLIO_USER_ROLE_OWNER:
      return intl.formatMessage({ id: 'util.i18n.constants.portfolio_user_role_label_owner', defaultMessage: 'Owner' });

    case PORTFOLIO_USER_ROLE_ADMIN:
      return intl.formatMessage({ id: 'util.i18n.constants.portfolio_user_role_label_administrator', defaultMessage: 'Administrator' });

    case PORTFOLIO_USER_ROLE_STANDARD:
      return intl.formatMessage({ id: 'util.i18n.constants.portfolio_user_role_label_standard_user', defaultMessage: 'Standard User' });

    case PORTFOLIO_USER_ROLE_VIEWER:
      return intl.formatMessage({ id: 'util.i18n.constants.portfolio_user_role_label_viewer', defaultMessage: 'Viewer' });

    default:
      return intl.formatMessage({ id: 'util.i18n.constants.portfolio_user_role_label_unspecified', defaultMessage: 'Unspecified' });
  }
};
